/**
 * AGC: Staking Admin Controls Component
 * AGC: Allows owner to pause/unpause staking, manage emergency withdrawals, and update StakingManager on NFT contracts
 * @author AGC (AI-Generated Code) with Cursor
 */

import React, { useState, useEffect } from 'react'
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useReadContract } from 'wagmi'
import { parseEther, formatEther } from 'viem'
import { useContractOwner } from '../../hooks/useContractOwner'
import { useDebugSettings } from '../../hooks/useDebugSettings'

// AGC: Contract addresses from environment
const STAKING_MANAGER_ADDRESS = (import.meta.env.VITE_STAKING_MANAGER_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const BUFF1155_ADDRESS = (import.meta.env.VITE_BUFF1155_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const CLASSIC_POTION_ADDRESS = (import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`

// AGC: StakingManagerV4 ABI (admin functions)
const STAKING_MANAGER_ABI = [
  {
    name: 'pause',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: []
  },
  {
    name: 'unpause',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: []
  },
  {
    name: 'paused',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'bool' }]
  },
  {
    name: 'emergencyWithdraw',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'token', type: 'address' },
      { name: 'amount', type: 'uint256' }
    ],
    outputs: []
  },
  {
    name: 'getContractOGBBalance',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  }
] as const

// AGC: Buff1155 ABI for setStakingManagerAddress
const BUFF1155_ABI = [
  {
    name: 'setStakingManagerAddress',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: '_stakingManagerAddress', type: 'address' }],
    outputs: []
  },
  {
    name: 'stakingManagerAddress',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }]
  },
  {
    name: 'owner',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }]
  }
] as const

// AGC: ClassicPotion1155 ABI for setStakingManager
const CLASSIC_POTION_ABI = [
  {
    name: 'setStakingManager',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'newStakingManager', type: 'address' }],
    outputs: []
  },
  {
    name: 'stakingManagerAddress',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }]
  },
  {
    name: 'owner',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }]
  }
] as const

export const StakingAdminControls: React.FC = () => {
  const { address, isConnected } = useAccount()
  const { writeContractAsync, isPending } = useWriteContract()
  const [pendingTxHash, setPendingTxHash] = useState<`0x${string}` | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  // AGC: Check if connected wallet is contract owner (for StakingManager)
  const { isOwner, ownerAddress, isLoading: isOwnerLoading } = useContractOwner()
  
  // AGC: Emergency withdraw form
  const [emergencyToken, setEmergencyToken] = useState('')
  const [emergencyAmount, setEmergencyAmount] = useState('')

  // AGC: New staking manager address input
  const [newStakingManagerAddress, setNewStakingManagerAddress] = useState('')

  // AGC: Emergency withdraw popup visibility toggles (default: off/hidden) - synced via WebSocket
  const { debugSettings, updateSetting } = useDebugSettings()
  const emergencyWithdrawPopupEnabled = debugSettings.showEmergencyWithdrawPopup // AGC: Legacy - kept for backward compatibility
  const stakingManagerButtonEnabled = debugSettings.showEmergencyWithdrawStakingManager
  const stakingRevenueShareButtonEnabled = debugSettings.showEmergencyWithdrawStakingRevenueShare
  
  // AGC: State for showing validation error message
  const [validationError, setValidationError] = useState<string | null>(null)
  
  // AGC: Clear validation error when both buttons are disabled
  useEffect(() => {
    if (!stakingManagerButtonEnabled && !stakingRevenueShareButtonEnabled) {
      setValidationError(null)
    }
  }, [stakingManagerButtonEnabled, stakingRevenueShareButtonEnabled])

  // AGC: Read paused status
  const { data: isPaused, refetch: refetchPaused } = useReadContract({
    address: STAKING_MANAGER_ADDRESS,
    abi: STAKING_MANAGER_ABI,
    functionName: 'paused',
    query: {
      enabled: isConnected && STAKING_MANAGER_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  }) as { data: boolean | undefined; refetch: () => void }

  // AGC: Read contract OGB balance
  const { data: contractBalance, refetch: refetchBalance } = useReadContract({
    address: STAKING_MANAGER_ADDRESS,
    abi: STAKING_MANAGER_ABI,
    functionName: 'getContractOGBBalance',
    query: {
      enabled: isConnected && STAKING_MANAGER_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  }) as { data: bigint | undefined; refetch: () => void }

  // AGC: Read Buff1155 current staking manager
  const { data: buff1155StakingManager, refetch: refetchBuff1155SM } = useReadContract({
    address: BUFF1155_ADDRESS,
    abi: BUFF1155_ABI,
    functionName: 'stakingManagerAddress',
    query: {
      enabled: isConnected && BUFF1155_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  }) as { data: string | undefined; refetch: () => void }

  // AGC: Read Buff1155 owner
  const { data: buff1155Owner } = useReadContract({
    address: BUFF1155_ADDRESS,
    abi: BUFF1155_ABI,
    functionName: 'owner',
    query: {
      enabled: isConnected && BUFF1155_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  }) as { data: string | undefined }

  // AGC: Read ClassicPotion1155 current staking manager
  const { data: classicPotionStakingManager, refetch: refetchClassicPotionSM } = useReadContract({
    address: CLASSIC_POTION_ADDRESS,
    abi: CLASSIC_POTION_ABI,
    functionName: 'stakingManagerAddress',
    query: {
      enabled: isConnected && CLASSIC_POTION_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  }) as { data: string | undefined; refetch: () => void }

  // AGC: Read ClassicPotion1155 owner
  const { data: classicPotionOwner } = useReadContract({
    address: CLASSIC_POTION_ADDRESS,
    abi: CLASSIC_POTION_ABI,
    functionName: 'owner',
    query: {
      enabled: isConnected && CLASSIC_POTION_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  }) as { data: string | undefined }

  const { data: txReceipt } = useWaitForTransactionReceipt({ hash: pendingTxHash || undefined })

  // AGC: Check if connected wallet is owner of Buff1155
  const isBuff1155Owner = address && buff1155Owner && address.toLowerCase() === buff1155Owner.toLowerCase()

  // AGC: Check if connected wallet is owner of ClassicPotion1155
  const isClassicPotionOwner = address && classicPotionOwner && address.toLowerCase() === classicPotionOwner.toLowerCase()

  // AGC: Handle transaction receipt
  React.useEffect(() => {
    if (txReceipt && pendingTxHash) {
      setSuccess('✅ Transaction confirmed!')
      setPendingTxHash(null)
      refetchPaused()
      refetchBalance()
      refetchBuff1155SM()
      refetchClassicPotionSM()
      setEmergencyToken('')
      setEmergencyAmount('')
    }
  }, [txReceipt, pendingTxHash, refetchPaused, refetchBalance, refetchBuff1155SM, refetchClassicPotionSM])

  // AGC: Pause staking
  const handlePause = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (STAKING_MANAGER_ADDRESS === '0x0000000000000000000000000000000000000000') {
      setError('StakingManager address not configured')
      return
    }

    try {
      setError(null)
      setSuccess(null)

      const hash = await writeContractAsync({
        address: STAKING_MANAGER_ADDRESS,
        abi: STAKING_MANAGER_ABI,
        functionName: 'pause',
        args: [],
      })

      setPendingTxHash(hash)
      setSuccess('Pause transaction submitted! Waiting for confirmation...')
    } catch (err: any) {
      console.error('Failed to pause:', err)
      setError(err.message || 'Failed to pause staking')
    }
  }

  // AGC: Unpause staking
  const handleUnpause = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (STAKING_MANAGER_ADDRESS === '0x0000000000000000000000000000000000000000') {
      setError('StakingManager address not configured')
      return
    }

    try {
      setError(null)
      setSuccess(null)

      const hash = await writeContractAsync({
        address: STAKING_MANAGER_ADDRESS,
        abi: STAKING_MANAGER_ABI,
        functionName: 'unpause',
        args: [],
      })

      setPendingTxHash(hash)
      setSuccess('Unpause transaction submitted! Waiting for confirmation...')
    } catch (err: any) {
      console.error('Failed to unpause:', err)
      setError(err.message || 'Failed to unpause staking')
    }
  }

  // AGC: Emergency withdraw
  const handleEmergencyWithdraw = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!emergencyToken || !emergencyAmount) {
      setError('Please fill in token address and amount')
      return
    }

    if (STAKING_MANAGER_ADDRESS === '0x0000000000000000000000000000000000000000') {
      setError('StakingManager address not configured')
      return
    }

    try {
      setError(null)
      setSuccess(null)

      const amountWei = parseEther(emergencyAmount)
      const hash = await writeContractAsync({
        address: STAKING_MANAGER_ADDRESS,
        abi: STAKING_MANAGER_ABI,
        functionName: 'emergencyWithdraw',
        args: [emergencyToken as `0x${string}`, amountWei],
      })

      setPendingTxHash(hash)
      setSuccess('Emergency withdraw transaction submitted! Waiting for confirmation...')
    } catch (err: any) {
      console.error('Failed to emergency withdraw:', err)
      setError(err.message || 'Failed to emergency withdraw')
    }
  }

  // AGC: Update Buff1155 StakingManager
  const handleUpdateBuff1155StakingManager = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!newStakingManagerAddress || !newStakingManagerAddress.startsWith('0x') || newStakingManagerAddress.length !== 42) {
      setError('Please enter a valid StakingManager address (0x...)')
      return
    }

    if (BUFF1155_ADDRESS === '0x0000000000000000000000000000000000000000') {
      setError('Buff1155 address not configured')
      return
    }

    try {
      setError(null)
      setSuccess(null)

      const hash = await writeContractAsync({
        address: BUFF1155_ADDRESS,
        abi: BUFF1155_ABI,
        functionName: 'setStakingManagerAddress',
        args: [newStakingManagerAddress as `0x${string}`],
      })

      setPendingTxHash(hash)
      setSuccess('Buff1155 StakingManager update submitted! Waiting for confirmation...')
    } catch (err: any) {
      console.error('Failed to update Buff1155 StakingManager:', err)
      setError(err.message || 'Failed to update Buff1155 StakingManager')
    }
  }

  // AGC: Update ClassicPotion1155 StakingManager
  const handleUpdateClassicPotionStakingManager = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!newStakingManagerAddress || !newStakingManagerAddress.startsWith('0x') || newStakingManagerAddress.length !== 42) {
      setError('Please enter a valid StakingManager address (0x...)')
      return
    }

    if (CLASSIC_POTION_ADDRESS === '0x0000000000000000000000000000000000000000') {
      setError('ClassicPotion1155 address not configured')
      return
    }

    try {
      setError(null)
      setSuccess(null)

      const hash = await writeContractAsync({
        address: CLASSIC_POTION_ADDRESS,
        abi: CLASSIC_POTION_ABI,
        functionName: 'setStakingManager',
        args: [newStakingManagerAddress as `0x${string}`],
      })

      setPendingTxHash(hash)
      setSuccess('ClassicPotion1155 StakingManager update submitted! Waiting for confirmation...')
    } catch (err: any) {
      console.error('Failed to update ClassicPotion1155 StakingManager:', err)
      setError(err.message || 'Failed to update ClassicPotion1155 StakingManager')
    }
  }

  // AGC: Update Both Contracts at once
  const handleUpdateBothStakingManagers = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!newStakingManagerAddress || !newStakingManagerAddress.startsWith('0x') || newStakingManagerAddress.length !== 42) {
      setError('Please enter a valid StakingManager address (0x...)')
      return
    }

    if (!isBuff1155Owner || !isClassicPotionOwner) {
      setError('You must be owner of both contracts to update both')
      return
    }

    try {
      setError(null)
      setSuccess(null)

      // AGC: Update Buff1155 first
      setSuccess('Updating Buff1155... (1/2)')
      const hash1 = await writeContractAsync({
        address: BUFF1155_ADDRESS,
        abi: BUFF1155_ABI,
        functionName: 'setStakingManagerAddress',
        args: [newStakingManagerAddress as `0x${string}`],
      })
      setPendingTxHash(hash1)
      
      // AGC: Wait a bit then update ClassicPotion1155
      setSuccess('Buff1155 submitted! Now updating ClassicPotion1155... (2/2)')
      const hash2 = await writeContractAsync({
        address: CLASSIC_POTION_ADDRESS,
        abi: CLASSIC_POTION_ABI,
        functionName: 'setStakingManager',
        args: [newStakingManagerAddress as `0x${string}`],
      })
      setPendingTxHash(hash2)
      setSuccess('Both contracts updated! Waiting for confirmations...')
    } catch (err: any) {
      console.error('Failed to update StakingManagers:', err)
      setError(err.message || 'Failed to update StakingManagers')
    }
  }

  if (!isConnected) {
    return (
      <div className="p-6 bg-slate-900 rounded-xl text-white">
        <h2 className="text-xl font-bold mb-4">⚙️ Staking Admin Controls</h2>
        <p className="text-slate-400">Please connect your wallet to access this feature.</p>
      </div>
    )
  }

  const shortenAddress = (addr: string): string => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`
  }

  return (
    <div className="p-6 bg-slate-900 rounded-xl text-white space-y-6">
      <div>
        <h2 className="text-xl font-bold mb-2">⚙️ Staking Admin Controls</h2>
        <p className="text-sm text-slate-400">
          Manage staking contract state and emergency operations. Owner only.
        </p>
      </div>

      {/* AGC: Owner Status Badge */}
      {isOwnerLoading ? (
        <div className="p-3 bg-slate-800 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
            <span className="text-sm text-slate-400">Checking owner status...</span>
          </div>
        </div>
      ) : isOwner ? (
        <div className="p-3 bg-green-900/30 border border-green-500 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-green-400 font-bold">✅ Contract Owner</span>
              <span className="text-xs text-slate-400 font-mono">{shortenAddress(address!)}</span>
            </div>
            <span className="text-xs text-green-400">Owner functions enabled</span>
          </div>
        </div>
      ) : (
        <div className="p-3 bg-red-900/30 border border-red-500 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-red-400 font-semibold">⚠️ Not Contract Owner</span>
              <p className="text-xs text-slate-400 mt-1">
                Contract owner: <span className="font-mono">{ownerAddress ? shortenAddress(ownerAddress) : 'Loading...'}</span>
              </p>
            </div>
            <span className="text-xs text-red-400">Owner functions disabled</span>
          </div>
        </div>
      )}

      {/* AGC: Status Messages */}
      {error && (
        <div className="p-3 bg-red-900/50 border border-red-500 rounded-lg text-red-200">
          ⚠️ {error}
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-900/50 border border-green-500 rounded-lg text-green-200">
          ✅ {success}
          {pendingTxHash && (
            <div className="mt-2">
              <a
                href={`https://sepolia.basescan.org/tx/${pendingTxHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline text-sm"
              >
                View on BaseScan
              </a>
            </div>
          )}
        </div>
      )}
      {isPending && pendingTxHash && (
        <div className="p-3 bg-blue-900/50 border border-blue-500 rounded-lg text-blue-200">
          ⏳ Transaction pending...
          <div className="mt-2">
            <a
              href={`https://sepolia.basescan.org/tx/${pendingTxHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline text-sm"
            >
              View on BaseScan
            </a>
          </div>
        </div>
      )}

      {/* AGC: Contract Status */}
      <section className="border border-slate-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3">📊 Contract Status</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
            <span className="text-slate-400">Status:</span>
            <span className={`font-semibold ${isPaused ? 'text-red-400' : 'text-green-400'}`}>
              {isPaused ? '⏸️ Paused' : '▶️ Active'}
            </span>
          </div>
          <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
            <span className="text-slate-400">Contract OGB Balance:</span>
            <span className="font-mono font-bold text-emerald-400">
              {contractBalance ? formatEther(contractBalance) : '0'} OGB
            </span>
          </div>
        </div>
      </section>

      {/* AGC: Update StakingManager on NFT Contracts - NEW SECTION */}
      <section className="border border-purple-700 rounded-lg p-4 bg-purple-900/10">
        <h3 className="text-lg font-semibold mb-3 text-purple-400">🔄 Update StakingManager on NFT Contracts</h3>
        <p className="text-sm text-slate-400 mb-4">
          Update the StakingManager address used for discount checks on Buff1155 and ClassicPotion1155 contracts. <strong className="text-purple-400">Owner only.</strong>
        </p>
        
        {/* AGC: Current StakingManager Addresses */}
        <div className="space-y-2 text-sm mb-4">
          <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
            <span className="text-slate-400">Current StakingManager:</span>
            <span className="font-mono text-xs text-blue-400">
              {shortenAddress(STAKING_MANAGER_ADDRESS)}
            </span>
          </div>
          <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
            <div className="flex items-center gap-2">
              <span className="text-slate-400">Buff1155 → StakingManager:</span>
              {isBuff1155Owner && <span className="text-xs text-green-400">✓ Owner</span>}
            </div>
            <span className={`font-mono text-xs ${buff1155StakingManager?.toLowerCase() === STAKING_MANAGER_ADDRESS.toLowerCase() ? 'text-green-400' : 'text-red-400'}`}>
              {buff1155StakingManager ? shortenAddress(buff1155StakingManager) : 'Loading...'}
              {buff1155StakingManager?.toLowerCase() === STAKING_MANAGER_ADDRESS.toLowerCase() ? ' ✅' : ' ⚠️'}
            </span>
          </div>
          <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
            <div className="flex items-center gap-2">
              <span className="text-slate-400">ClassicPotion → StakingManager:</span>
              {isClassicPotionOwner && <span className="text-xs text-green-400">✓ Owner</span>}
            </div>
            <span className={`font-mono text-xs ${classicPotionStakingManager?.toLowerCase() === STAKING_MANAGER_ADDRESS.toLowerCase() ? 'text-green-400' : 'text-red-400'}`}>
              {classicPotionStakingManager ? shortenAddress(classicPotionStakingManager) : 'Loading...'}
              {classicPotionStakingManager?.toLowerCase() === STAKING_MANAGER_ADDRESS.toLowerCase() ? ' ✅' : ' ⚠️'}
            </span>
          </div>
        </div>

        {/* AGC: New StakingManager Address Input */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-slate-400 mb-2">New StakingManager Address</label>
            <input
              type="text"
              value={newStakingManagerAddress}
              onChange={(e) => setNewStakingManagerAddress(e.target.value)}
              placeholder="0x..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg font-mono text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">
              Current: {shortenAddress(STAKING_MANAGER_ADDRESS)}
            </p>
          </div>

          {/* AGC: Update Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button
              onClick={handleUpdateBuff1155StakingManager}
              disabled={isPending || !newStakingManagerAddress || !isBuff1155Owner}
              className="py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold transition text-sm"
            >
              {isPending ? 'Processing...' : !isBuff1155Owner ? '🔒 Not Owner' : '🎴 Update Buff1155'}
            </button>
            <button
              onClick={handleUpdateClassicPotionStakingManager}
              disabled={isPending || !newStakingManagerAddress || !isClassicPotionOwner}
              className="py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold transition text-sm"
            >
              {isPending ? 'Processing...' : !isClassicPotionOwner ? '🔒 Not Owner' : '🧪 Update Potion'}
            </button>
            <button
              onClick={handleUpdateBothStakingManagers}
              disabled={isPending || !newStakingManagerAddress || !isBuff1155Owner || !isClassicPotionOwner}
              className="py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-bold transition text-sm"
            >
              {isPending ? 'Processing...' : (!isBuff1155Owner || !isClassicPotionOwner) ? '🔒 Not Owner' : '🔄 Update Both'}
            </button>
          </div>

          {/* AGC: Owner Warning */}
          {(!isBuff1155Owner || !isClassicPotionOwner) && (
            <div className="p-3 bg-yellow-900/30 border border-yellow-500 rounded-lg mt-3">
              <p className="text-yellow-400 text-sm">
                ⚠️ You must be the owner of both contracts to update them.
              </p>
              <div className="text-xs text-slate-400 mt-2 space-y-1">
                <p>Buff1155 owner: {buff1155Owner ? shortenAddress(buff1155Owner) : 'Loading...'}</p>
                <p>ClassicPotion owner: {classicPotionOwner ? shortenAddress(classicPotionOwner) : 'Loading...'}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* AGC: Pause/Unpause Controls */}
      <section className="border border-slate-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3">⏸️ Pause/Unpause Staking - Owner Only</h3>
        <p className="text-sm text-slate-400 mb-4">
          Pause staking operations in case of emergency. When paused, users cannot stake or unstake.
        </p>
        {!isOwner && (
          <div className="p-3 bg-red-900/30 border border-red-500 rounded-lg mb-4">
            <p className="text-red-400 text-sm">
              ⚠️ Only the contract owner can pause/unpause staking.
            </p>
          </div>
        )}
        <div className="flex gap-3">
          <button
            onClick={handlePause}
            disabled={isPending || isPaused || !isOwner}
            className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold transition"
          >
            {isPending ? 'Processing...' : isPaused ? 'Already Paused' : !isOwner ? 'Owner Only' : '⏸️ Pause'}
          </button>
          <button
            onClick={handleUnpause}
            disabled={isPending || !isPaused || !isOwner}
            className="flex-1 py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold transition"
          >
            {isPending ? 'Processing...' : !isPaused ? 'Already Active' : !isOwner ? 'Owner Only' : '▶️ Unpause'}
          </button>
        </div>
      </section>

      {/* AGC: Emergency Withdraw Popup Toggle */}
      <section className="border border-orange-700 rounded-lg p-4 bg-orange-900/10">
        <h3 className="text-lg font-semibold mb-3 text-orange-400">🚨 Emergency Withdraw Popup Control</h3>
        <p className="text-sm text-slate-400 mb-4">
          Toggle visibility of the emergency withdraw button on Home page and TopNav. Default: Hidden.
        </p>
        <div className="space-y-3">
          {/* AGC: Validation Error Message */}
          {validationError && (
            <div className="p-3 bg-yellow-900/30 border border-yellow-500/50 rounded-lg">
              <p className="text-yellow-200 text-sm font-semibold">⚠️ {validationError}</p>
            </div>
          )}
          
          {/* AGC: StakingManager Button Toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
            <div>
              <span className="text-slate-300 font-semibold">📊 StakingManager Button:</span>
              <span className={`ml-2 font-bold ${stakingManagerButtonEnabled ? 'text-green-400' : 'text-red-400'}`}>
                {stakingManagerButtonEnabled ? 'Visible' : 'Hidden'}
              </span>
              <p className="text-xs text-slate-500 mt-1">2-of-2 signer approval required</p>
            </div>
            <button
              onClick={() => {
                setValidationError(null) // AGC: Clear any previous errors
                const newValue = !stakingManagerButtonEnabled
                
                // AGC: Validation: Cannot enable if the other button is already enabled
                if (newValue && stakingRevenueShareButtonEnabled) {
                  setValidationError('Please hide the StakingRevenueShare button first before showing StakingManager button.')
                  return
                }
                
                updateSetting('showEmergencyWithdrawStakingManager', newValue)
              }}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                stakingManagerButtonEnabled
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {stakingManagerButtonEnabled ? 'Hide' : 'Show'}
            </button>
          </div>
          
          {/* AGC: StakingRevenueShare Button Toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
            <div>
              <span className="text-slate-300 font-semibold">💰 StakingRevenueShare Button:</span>
              <span className={`ml-2 font-bold ${stakingRevenueShareButtonEnabled ? 'text-green-400' : 'text-red-400'}`}>
                {stakingRevenueShareButtonEnabled ? 'Visible' : 'Hidden'}
              </span>
              <p className="text-xs text-slate-500 mt-1">1 signer approval required</p>
            </div>
            <button
              onClick={() => {
                setValidationError(null) // AGC: Clear any previous errors
                const newValue = !stakingRevenueShareButtonEnabled
                
                // AGC: Validation: Cannot enable if the other button is already enabled
                if (newValue && stakingManagerButtonEnabled) {
                  setValidationError('Please hide the StakingManager button first before showing StakingRevenueShare button.')
                  return
                }
                
                updateSetting('showEmergencyWithdrawStakingRevenueShare', newValue)
              }}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                stakingRevenueShareButtonEnabled
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {stakingRevenueShareButtonEnabled ? 'Hide' : 'Show'}
            </button>
          </div>
          
          {/* AGC: Info Message */}
          <div className="p-3 bg-slate-800 rounded-lg">
            <p className="text-xs text-slate-400">
              <strong className="text-yellow-400">Note:</strong> Only one button can be visible at a time. Both can be hidden.
            </p>
          </div>
        </div>
      </section>

      {/* AGC: Emergency Withdraw */}
      <section className="border border-red-700 rounded-lg p-4 bg-red-900/10">
        <h3 className="text-lg font-semibold mb-3 text-red-400">🚨 Emergency Withdraw</h3>
        <p className="text-sm text-slate-400 mb-4">
          <strong className="text-red-400">WARNING:</strong> This allows the owner to withdraw tokens from the contract. Use only in emergencies.
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Token Address</label>
            <input
              type="text"
              value={emergencyToken}
              onChange={(e) => setEmergencyToken(e.target.value)}
              placeholder="0x..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg font-mono text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Amount</label>
            <input
              type="text"
              value={emergencyAmount}
              onChange={(e) => setEmergencyAmount(e.target.value)}
              placeholder="1000"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-lg"
            />
          </div>
          <button
            onClick={handleEmergencyWithdraw}
            disabled={isPending || !emergencyToken || !emergencyAmount || !isOwner}
            className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-bold transition"
          >
            {isPending ? 'Processing...' : !isOwner ? 'Owner Only' : '🚨 Emergency Withdraw'}
          </button>
        </div>
      </section>
    </div>
  )
}

export default StakingAdminControls
